import { Tag, Button } from 'antd';
import { FilterOutlined, CloseOutlined } from '@ant-design/icons';

// Active-filters strip rendered between the toolbar and the table.
// Reads the consolidated filter state (column filters + global search)
// and exposes inline "clear one" + "clear all" affordances.
//
// Designed to be a quiet, scannable strip — slate background, soft
// brand-blue accent on the chips. Hidden entirely when nothing is
// active so it doesn't introduce vertical noise.

function chipLabelFor(filterKey, filterValues, leafColumns) {
  const col = (leafColumns || []).find(
    (c) => (c.key || c.dataIndex) === filterKey
  );
  const colTitle =
    col && typeof col.title === 'string' ? col.title : String(filterKey);
  const display = Array.isArray(filterValues)
    ? filterValues
        .map((v) => {
          // Find the user-facing label for the filter value if the
          // column defined an explicit `filters` array.
          if (col && Array.isArray(col.filters)) {
            const f = col.filters.find((x) => x.value === v);
            if (f && f.text !== undefined) return String(f.text);
          }
          return String(v);
        })
        .join(', ')
    : String(filterValues);
  return { colTitle, display };
}

export default function ErpDataGridFilterChips({
  filters,
  searchText,
  leafColumns,
  onClearFilter,
  onClearSearch,
  onClearAll
}) {
  const activeFilterEntries = Object.entries(filters || {}).filter(
    ([, v]) => Array.isArray(v) && v.length > 0
  );
  const hasSearch = Boolean(searchText && String(searchText).trim().length);

  if (!hasSearch && activeFilterEntries.length === 0) return null;

  return (
    <div className="erp-grid__filter-chips" role="region" aria-label="Active filters">
      <span className="erp-grid__filter-chips-label">
        <FilterOutlined aria-hidden="true" /> Active
      </span>

      {hasSearch ? (
        <Tag
          closable
          onClose={(e) => {
            e.preventDefault();
            if (typeof onClearSearch === 'function') onClearSearch();
          }}
          closeIcon={<CloseOutlined style={{ fontSize: 10 }} />}
          className="erp-grid__chip"
        >
          <span className="erp-grid__chip-key">Search</span>
          <span className="erp-grid__chip-sep">:</span>
          <span className="erp-grid__chip-val">"{searchText}"</span>
        </Tag>
      ) : null}

      {activeFilterEntries.map(([key, vals]) => {
        const { colTitle, display } = chipLabelFor(key, vals, leafColumns);
        return (
          <Tag
            key={key}
            closable
            onClose={(e) => {
              e.preventDefault();
              if (typeof onClearFilter === 'function') onClearFilter(key);
            }}
            closeIcon={<CloseOutlined style={{ fontSize: 10 }} />}
            className="erp-grid__chip"
          >
            <span className="erp-grid__chip-key">{colTitle}</span>
            <span className="erp-grid__chip-sep">:</span>
            <span className="erp-grid__chip-val">{display}</span>
          </Tag>
        );
      })}

      {typeof onClearAll === 'function' ? (
        <Button
          type="link"
          size="small"
          onClick={onClearAll}
          className="erp-grid__filter-chips-clear"
        >
          Clear all
        </Button>
      ) : null}
    </div>
  );
}
